/**
 * Light obfuscation for invoice IDs used in public shareable URLs.
 * Uses XOR with a rotating key followed by URL-safe base64 encoding.
 * This is not military-grade encryption — it simply hides the raw numeric ID
 * from casual inspection in the URL bar.
 */

const KEY = "YCP_INV_2024_MC";

/**
 * Encodes an invoice ID into a URL-safe string.
 * @param {string|number} id
 * @returns {string} URL-safe encoded string
 */
export function encodeInvId(id) {
  const str = String(id);
  const keyArr = KEY.split("").map((c) => c.charCodeAt(0));
  const xored = str
    .split("")
    .map((c, i) => String.fromCharCode(c.charCodeAt(0) ^ keyArr[i % keyArr.length]))
    .join("");
  return btoa(xored)
    .replace(/=/g, "")
    .replace(/\+/g, "-")
    .replace(/\//g, "_");
}

/**
 * Decodes an encoded invoice ID back to its original value.
 * @param {string} encoded
 * @returns {string|null} decoded ID string, or null if decoding fails
 */
export function decodeInvId(encoded) {
  try {
    const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const rem = base64.length % 4;
    const padded = rem === 0 ? base64 : base64 + "=".repeat(4 - rem);
    const xored = atob(padded);
    const keyArr = KEY.split("").map((c) => c.charCodeAt(0));
    return xored
      .split("")
      .map((c, i) => String.fromCharCode(c.charCodeAt(0) ^ keyArr[i % keyArr.length]))
      .join("");
  } catch {
    return null;
  }
}
